// The Daily Live Price board — one row per index, what that class brought
// today, with a toggle per category. Lives under the ticker on the homepage.
//
// Owner, 2026-10-07: "Daily Live Price (Wtd. Average)… a board under the
// ticker on the home page with toggles for each index category… sundays as
// dashes." Rob asked for it: "our live number that changes throughout the
// day", not the seven-day average.
//
// ⚠️ SAME COLUMNS AS THE INDEX BOARD, NEVER THE SAME TABLE. Index · Latest ·
// Change · Open · High · Low · Previous · Head · Lots · Barns · Date. The
// codes carry -D and the heading says what it is, so nobody reads a day as
// the benchmark.
//
// ⚠️ PRERENDERED HOMEPAGE: fetches after mount through /api/market only, and
// renders nothing until it has rows, so the served HTML and the first client
// render agree.
//
// ⚠️ NO ROUTER LINK, EVER. The homepage is its own entry (entry-rbticker.tsx)
// hydrated with NO <Router>; a react-router <Link> here throws on hydration
// and the whole homepage goes blank — which it did on 2026-10-07 the first
// time this board shipped. Plain <a href>, like every other link on that page.

import { useEffect, useState } from "react";
import { indexShortName } from "@/lib/rbIndexNames";

import {
  DAILY_CATEGORIES, type DailyCategory, type DailyQuote, dailyCategory, fetchDailyQuotes,
} from "@/lib/rbDailyPrice";
import { cnt, dir, lastPx, mdy, px, signedPct, signedPx } from "./quoteFormat";
import "./indexPage.css";

/** Same cadence as the index board: 20 s against a 20 s server cache. */
const POLL_MS = 20_000;

function wd(iso: string | null): string {
  if (!iso) return "last week";
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? "last week" : `last ${d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}`;
}

function Row({ q }: { q: DailyQuote }) {
  // Week over week (owner, 2026-10-08: "is it the change from yesterday or
  // tuesday vs the last tuesday?"): Latest against the same weekday last week.
  const d = dir(q.changeWk);
  const hd = dir(q.headChgWkPct);
  // Owner, 2026-10-07: "Can the date be at the far left of the table" — and a
  // click goes to that day's page, every change with its time.
  const day = `/index/${q.slug}/daily/${q.asOf}`;
  return (
    <tr>
      <td className="rbq-date">
        <a href={day}>{mdy(q.asOf)}</a>
        {q.settled && q.todayPublication === "INSUFFICIENT_VOLUME" && (
          <small title={`today so far: ${cnt(q.todayHead)} head in ${cnt(q.todayLots)} lots, under the ${q.floor ?? 10}-head floor`}> · today {cnt(q.todayHead)} hd</small>
        )}
      </td>
      <td className="rbq-ix">
        {/* The name only — no ticker mnemonic (owner, 2026-10-08: "remove the ticker mnemonic and just state the name"). */}
        {/* "Daily Live" in every name, so no one reads a one-day figure as the seven-day index (owner, 2026-10-08). */}
        <a href={day} title={`${q.code}-D`}><b>Daily Live {indexShortName(q.name)}</b></a>
      </td>
      <td className="rbq-n rbq-last">{lastPx(q.last, q.settled)}</td>
      <td className="rbq-n rbq-chg" data-dir={d ?? undefined} title={q.wkAsOf ? `against ${wd(q.wkAsOf)}'s final, ${px(q.wkCwt)}` : "no final on the same weekday last week"}>
        {signedPx(q.changeWk)}<small>{signedPct(q.changeWkPct)}</small>
      </td>
      <td className="rbq-n">{px(q.open)}</td>
      <td className="rbq-n">{px(q.high)}</td>
      <td className="rbq-n">{px(q.low)}</td>
      <td className="rbq-n">{px(q.prev)}</td>
      {/* Receipts against the same weekday last week (owner, 2026-10-08). */}
      <td className="rbq-n rbdp-head" data-dir={hd ?? undefined} title={q.wkHead != null ? `${cnt(q.wkHead)} head ${wd(q.wkAsOf)}` : undefined}>
        {cnt(q.head)}<small>{q.headChgWkPct == null ? "" : `${q.headChgWkPct > 0 ? "+" : q.headChgWkPct < 0 ? "−" : ""}${Math.abs(Math.round(q.headChgWkPct))}%`}</small>
      </td>
      <td className="rbq-n">{cnt(q.lots)}</td>
      <td className="rbq-n">{cnt(q.barns)}</td>
    </tr>
  );
}

export default function DailyPriceBoard({ poll = true }: { poll?: boolean }) {
  const [quotes, setQuotes] = useState<DailyQuote[] | null>(null);
  const [cat, setCat] = useState<DailyCategory>("headline");

  useEffect(() => {
    let on = true;
    const pull = () => {
      if (document.visibilityState === "hidden") return;
      fetchDailyQuotes().then((q) => { if (on && q.length) setQuotes(q); }).catch(() => { /* keep what we have */ });
    };
    pull();
    if (!poll) return () => { on = false; };
    const t = window.setInterval(pull, POLL_MS);
    return () => { on = false; window.clearInterval(t); };
  }, [poll]);

  if (!quotes) return null;
  const shown = quotes.filter((q) => dailyCategory(q) === cat);
  const live = shown.some((q) => !q.settled);

  return (
    <div className="rbdp-home">
      <section className="rbq rbdp" aria-label="Daily Live Price">
        <div className="rbq-bar">
          <h2>Daily Live Price <span>Wtd. Average · $/cwt · {live ? "building through today" : "last sale day's final"}</span></h2>
          <div className="rbq-ranges" role="group" aria-label="Which indexes">
            {DAILY_CATEGORIES.map((c) => (
              <button key={c.key} type="button" className="rbq-range" data-on={cat === c.key ? "" : undefined}
                      onClick={() => setCat(c.key)}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="rbq-tw">
          <table className="rbq-t">
            <thead>
              <tr>
                <th>Date</th>
                <th>Index</th>
                <th className="rbq-n">Latest</th>
                <th className="rbq-n" title="Latest against the same weekday last week's final, in dollars per cwt and percent">WoW Change</th>
                <th className="rbq-n">Open</th>
                <th className="rbq-n">High</th>
                <th className="rbq-n">Low</th>
                <th className="rbq-n">Previous</th>
                <th className="rbq-n" title="Today's head, and the change against the same weekday last week">Head</th>
                <th className="rbq-n">Lots</th>
                <th className="rbq-n">Barns</th>
              </tr>
            </thead>
            <tbody>{shown.map((q) => <Row key={q.slug} q={q} />)}</tbody>
          </table>
        </div>
        {/* No note under the table (owner, 2026-10-14: "Remove this paragraph"). */}
      </section>
    </div>
  );
}
